import type { Translate } from "@src/i18n/translate";
import type { AnimationMode } from "./animation";
import type { PlaybackState } from "./playback";

export interface PlaybackControlsViewProps {
  showBasis: boolean;
  showGrid: boolean;
  playbackMode: AnimationMode;
  playbackStatus: PlaybackState["status"];
  canPlay: boolean;
  t: Translate;
  visualizationReady: boolean;
  onTogglePlayback: () => void;
  onResetTransform: () => void;
  onAnimationModeChange: (mode: AnimationMode) => void;
  onBasisVisibilityChange: (visible: boolean) => void;
  onGridVisibilityChange: (visible: boolean) => void;
}

export function PlaybackControlsView({
  showBasis,
  showGrid,
  playbackMode,
  playbackStatus,
  canPlay,
  t,
  visualizationReady,
  onTogglePlayback,
  onResetTransform,
  onAnimationModeChange,
  onBasisVisibilityChange,
  onGridVisibilityChange,
}: PlaybackControlsViewProps) {
  const playbackLabel =
    playbackStatus === "playing"
      ? "pause"
      : playbackStatus === "paused"
        ? "resume"
        : "apply";
  const playbackAria =
    playbackStatus === "playing"
      ? "pauseAnimation"
      : playbackStatus === "paused"
        ? "resumeAnimation"
        : "applyTransform";
  return (
    <section className="control-cluster">
      <fieldset className="animation-block">
        <legend>{t("animation")}</legend>
        <div className="animation-body">
          <div
            className="transport"
            role="group"
            aria-label={t("animationControls")}
          >
            <button
              type="button"
              onClick={onTogglePlayback}
              aria-label={t(playbackAria)}
              data-playback-status={playbackStatus}
              disabled={!canPlay || !visualizationReady}
            >
              {t(playbackLabel)}
            </button>
            <button
              type="button"
              onClick={onResetTransform}
              aria-label={t("resetTransform")}
            >
              {t("reset")}
            </button>
          </div>
          <label className="mode-row" htmlFor="animation-mode">
            <span className="mode-label">{t("method")}</span>
            <select
              id="animation-mode"
              name="animation-mode"
              aria-label={t("animationMethod")}
              value={playbackMode}
              onChange={(event) => {
                onAnimationModeChange(
                  event.target.value === "composed" ? "composed" : "steps",
                );
              }}
            >
              <option value="steps">{t("steps")}</option>
              <option value="composed">{t("composed")}</option>
            </select>
          </label>
        </div>
      </fieldset>
      <div className="visibility-toggles">
        <label className="toggle-row" htmlFor="show-basis">
          <span className="toggle-label">{t("basis")}</span>
          <input
            id="show-basis"
            type="checkbox"
            checked={showBasis}
            onChange={(event) => {
              onBasisVisibilityChange(event.target.checked);
            }}
          />
        </label>
        <label className="toggle-row" htmlFor="show-grid">
          <span className="toggle-label">{t("grid")}</span>
          <input
            id="show-grid"
            type="checkbox"
            checked={showGrid}
            onChange={(event) => {
              onGridVisibilityChange(event.target.checked);
            }}
          />
        </label>
      </div>
    </section>
  );
}
